import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Car, TrendingUp, TrendingDown, MapPin, AlertCircle, Maximize2, X } from 'lucide-react';
import { api } from '../../utils/api';
import StatsCard from '../Dashboard/components/StatsCard';
import HotspotMap from '../../components/HotspotMap';
import usePersistentFilters from '../../hooks/usePersistentFilters';

const Hotspot = () => {
  const { filters, handleFilterChange } = usePersistentFilters(
    {
      timeRange: '1day',
      developerFilter: 'Exclude',
      statusFilter: 'COMPLETED',
      cancelledByFilter: 'All',
      mapMode: 'cluster',
      groupTrips: true,
      regionalOfficeFilter: 'All'
    },
    'hotspot-filters',
    { clearOnExpiry: ['timeRange'] }
  );

  const [isMapExpanded, setIsMapExpanded] = useState(false);

  const { data: regionalOfficesData } = useQuery(
    'regional-offices',
    () => api.get('/regional-offices', { params: { limit: 1000 } }),
    { refetchInterval: false, refetchOnWindowFocus: false }
  );

  const { data, isLoading } = useQuery(
    ['hotspot-stats', filters.timeRange, filters.developerFilter, filters.groupTrips, filters.regionalOfficeFilter],
    () => api.get('/hotspots', {
      params: {
        timeRange: filters.timeRange,
        developerOnly: filters.developerFilter,
        groupTrips: filters.groupTrips,
        regionalOffice: filters.regionalOfficeFilter !== 'All' ? filters.regionalOfficeFilter : undefined
      }
    }),
    { refetchInterval: false, refetchOnWindowFocus: false }
  );

  const stats = data?.data?.data || {};

  // Filter locations on the frontend based on status and cancelledBy filters
  const tripLocations = React.useMemo(() => {
    const allTripLocations = data?.data?.data?.locations || [];
    let filtered = allTripLocations;

    // Apply status filter
    if (filters.statusFilter && filters.statusFilter !== 'ALL') {
      filtered = filtered.filter(trip => {
        if (filters.statusFilter === 'CANCELLED') {
          return trip.status && trip.status.match(/CANCELLED/i);
        }
        if (filters.statusFilter === 'BOOKING') {
          return trip.location && Array.isArray(trip.location) && trip.location.length >= 2;
        }
        if (filters.statusFilter === 'BOOKING_COMPLETED') {
          return trip.location && Array.isArray(trip.location) && trip.location.length >= 2 && trip.status === 'COMPLETED';
        }
        if (filters.statusFilter === 'BOOKING_CANCELLED') {
          return trip.location && Array.isArray(trip.location) && trip.location.length >= 2 && trip.status && trip.status.match(/CANCELLED/i);
        }
        return trip.status === filters.statusFilter;
      });
    }

    // Apply cancelledBy filter only when status is CANCELLED
    if (filters.statusFilter === 'CANCELLED' && filters.cancelledByFilter && filters.cancelledByFilter !== 'All') {
      filtered = filtered.filter(trip => {
        return trip.cancelledBy && trip.cancelledBy.toUpperCase() === filters.cancelledByFilter.toUpperCase();
      });
    }

    return filtered;
  }, [data, filters.statusFilter, filters.cancelledByFilter]);

  const statsCards = [
    {
      title: 'Total Trips',
      value: stats.totalTrips ?? 0,
      icon: Car,
      color: 'primary',
      locations: stats.locations?.total || []
    },
    {
      title: 'Completed Trips',
      value: stats.completedTrips ?? 0,
      icon: TrendingUp,
      color: 'success',
      locations: stats.locations?.completed || []
    },
    {
      title: 'Cancelled Trips',
      value: stats.cancelledTrips ?? 0,
      icon: TrendingDown,
      color: 'danger',
      locations: stats.locations?.cancelled || []
    },
    {
      title: 'Diverged Trips',
      value: stats.divergedTrips ?? 0,
      icon: MapPin,
      color: 'warning',
      locations: stats.locations?.diverged || []
    },
    {
      title: 'Unaccepted Trips',
      value: stats.unacceptedTrips ?? 0,
      icon: AlertCircle,
      color: 'danger',
      locations: stats.locations?.unaccepted || []
    }
  ];

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <h1 className="page-title">Hotspot Analytics</h1>
          <p className="page-subtitle">Trip statistics and insights</p>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex space-x-2">
          {[
            { key: '1day', label: '1 Day' },
            { key: '7days', label: '7 Days' },
            { key: '15days', label: '15 Days' },
            { key: '1month', label: '1 Month' },
            { key: '2months', label: '2 Months' }
          ].map((p) => (
            <button
              key={p.key}
              onClick={() => handleFilterChange('timeRange', p.key)}
              className={`px-3 py-1 text-sm rounded-md ${filters.timeRange === p.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex items-center space-x-4 flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Regional Office:</span>
            <select
              value={filters.regionalOfficeFilter}
              onChange={(e) => handleFilterChange('regionalOfficeFilter', e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="All">All Offices</option>
              {regionalOfficesData?.data?.data?.offices?.map((office) => (
                <option key={office._id} value={office._id}>
                  {office.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Developer Trips:</span>
            <div className="flex space-x-2">
              {[
                { key: 'Exclude', label: 'Exclude' },
                { key: 'Include', label: 'Include' },
                { key: 'Only', label: 'Only Developer' }
              ].map((option) => (
                <button
                  key={option.key}
                  onClick={() => handleFilterChange('developerFilter', option.key)}
                  className={`px-3 py-1 text-sm rounded-md ${filters.developerFilter === option.key
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {statsCards.map((card, index) => (
          <StatsCard key={index} {...card} loading={isLoading} />
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Hotspot Locations</h2>
          <div className="flex items-center space-x-3">
            {filters.statusFilter === 'CANCELLED' && (
              <select
                value={filters.cancelledByFilter}
                onChange={(e) => handleFilterChange('cancelledByFilter', e.target.value)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="All">All Cancelled</option>
                <option value="DRIVER">Cancelled By Driver</option>
                <option value="PASSENGER">Cancelled By Passenger</option>
                <option value="SYSTEM">Cancelled By System</option>
              </select>
            )}
            {['BOOKING', 'BOOKING_COMPLETED', 'BOOKING_CANCELLED'].includes(filters.statusFilter) && (
              <select
                value={filters.statusFilter}
                onChange={(e) => handleFilterChange('statusFilter', e.target.value)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="BOOKING">All Bookings</option>
                <option value="BOOKING_COMPLETED">Booking Completed</option>
                <option value="BOOKING_CANCELLED">Booking Cancelled</option>
              </select>
            )}
            <select
              value={
                ['BOOKING', 'BOOKING_COMPLETED', 'BOOKING_CANCELLED'].includes(filters.statusFilter)
                  ? 'BOOKING'
                  : filters.statusFilter
              }
              onChange={(e) => {
                const val = e.target.value;
                handleFilterChange('statusFilter', val);
              }}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="ALL">All Status</option>
              <option value="COMPLETED">Completed</option>
              <option value="DIVERGED">Diverged</option>
              <option value="PENDING">Waiting for Driver</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="BOOKING">Booking</option>
            </select>
            <select
              value={filters.mapMode}
              onChange={(e) => handleFilterChange('mapMode', e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="cluster">Cluster</option>
              <option value="pins">Pins</option>
              <option value="heatmap">HeatMap</option>
            </select>
            <button
              onClick={() => handleFilterChange('groupTrips', !filters.groupTrips)}
              disabled={filters.statusFilter === 'COMPLETED' || filters.statusFilter === 'BOOKING_COMPLETED'}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${(filters.statusFilter === 'COMPLETED' || filters.statusFilter === 'BOOKING_COMPLETED')
                  ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                  : filters.groupTrips
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                }`}
              title={(filters.statusFilter === 'COMPLETED' || filters.statusFilter === 'BOOKING_COMPLETED') ? 'Grouping not available for Completed trips' : ''}
            >
              {filters.groupTrips && filters.statusFilter !== 'COMPLETED' && filters.statusFilter !== 'BOOKING_COMPLETED' ? 'Grouped' : 'Ungrouped'}
            </button>
            <button
              onClick={() => setIsMapExpanded(true)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Expand Map"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        <HotspotMap
          hotspots={stats.hotspots || []}
          tripLocations={tripLocations}
          statusFilter={filters.statusFilter}
          mapMode={filters.mapMode}
          height="500px"
          isGrouped={filters.statusFilter !== 'COMPLETED' && filters.statusFilter !== 'BOOKING_COMPLETED' && (filters.groupTrips || filters.statusFilter === 'BOOKING' || filters.statusFilter === 'BOOKING_CANCELLED')}
        />
      </div>

      {/* Full-screen Map Modal */}
      {isMapExpanded && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full h-full max-w-[95vw] max-h-[95vh] flex flex-col" style={{ zIndex: 10000 }}>
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold">Hotspot Map - Full View</h2>
              <div className="flex items-center space-x-3">
                {filters.statusFilter === 'CANCELLED' && (
                  <select
                    value={filters.cancelledByFilter}
                    onChange={(e) => handleFilterChange('cancelledByFilter', e.target.value)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="All">All Cancelled</option>
                    <option value="DRIVER">Cancelled By Driver</option>
                    <option value="PASSENGER">Cancelled By Passenger</option>
                    <option value="SYSTEM">Cancelled By System</option>
                  </select>
                )}
                {['BOOKING', 'BOOKING_COMPLETED', 'BOOKING_CANCELLED'].includes(filters.statusFilter) && (
                  <select
                    value={filters.statusFilter}
                    onChange={(e) => handleFilterChange('statusFilter', e.target.value)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="BOOKING">All Bookings</option>
                    <option value="BOOKING_COMPLETED">Booking Completed</option>
                    <option value="BOOKING_CANCELLED">Booking Cancelled</option>
                  </select>
                )}
                <select
                  value={
                    ['BOOKING', 'BOOKING_COMPLETED', 'BOOKING_CANCELLED'].includes(filters.statusFilter)
                      ? 'BOOKING'
                      : filters.statusFilter
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    handleFilterChange('statusFilter', val);
                  }}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="ALL">All Status</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="DIVERGED">Diverged</option>
                  <option value="PENDING">Waiting for Driver</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="BOOKING">Booking</option>
                </select>
                <select
                  value={filters.mapMode}
                  onChange={(e) => handleFilterChange('mapMode', e.target.value)}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="cluster">Cluster</option>
                  <option value="pins">Pins</option>
                  <option value="heatmap">HeatMap</option>
                </select>
                <button
                  onClick={() => handleFilterChange('groupTrips', !filters.groupTrips)}
                  disabled={filters.statusFilter === 'COMPLETED' || filters.statusFilter === 'BOOKING_COMPLETED'}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${(filters.statusFilter === 'COMPLETED' || filters.statusFilter === 'BOOKING_COMPLETED')
                      ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                      : filters.groupTrips
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                    }`}
                >
                  {filters.groupTrips && filters.statusFilter !== 'COMPLETED' && filters.statusFilter !== 'BOOKING_COMPLETED' ? 'Grouped' : 'Ungrouped'}
                </button>
                <button
                  onClick={() => setIsMapExpanded(false)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 p-4">
              <HotspotMap
                hotspots={stats.hotspots || []}
                tripLocations={tripLocations}
                statusFilter={filters.statusFilter}
                mapMode={filters.mapMode}
                height="100%"
                isGrouped={filters.statusFilter !== 'COMPLETED' && filters.statusFilter !== 'BOOKING_COMPLETED' && (filters.groupTrips || filters.statusFilter === 'BOOKING' || filters.statusFilter === 'BOOKING_CANCELLED')}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hotspot;